import { useEffect, useRef, useState } from "react";

const credentials = [
  {
    icon: "ri-scales-3-line",
    title: "Abogado – Universidad del Zulia (LUZ)",
    desc: "Sólida formación jurídica que le otorgó la capacidad de analizar cada situación con precisión y rigor profesional.",
  },
  {
    icon: "ri-bank-line",
    title: "Magíster en Banca y Finanzas – URU",
    desc: "Especialización en el mundo financiero y empresarial, con más de 35 años de experiencia en Riesgos y Seguros.",
  },
  {
    icon: "ri-graduation-cap-line",
    title: "Docente Universitario & Empresario Ganadero",
    desc: "Formador de generaciones y hombre de campo, arraigado en Villa del Rosario, Zulia, Venezuela.",
  },
];

export default function AutorSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="autor" ref={ref} className="py-28 md:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-16">

        {/* Section label */}
        <div className="flex items-center gap-4 mb-16">
          <div className="h-px flex-1" style={{ background: "linear-gradient(90deg, transparent, #D4AF37)" }} />
          <p className="text-xs font-semibold tracking-widest uppercase" style={{ color: "#D4AF37", letterSpacing: "4px" }}>
            El Hombre Detrás de la Historia
          </p>
          <div className="h-px flex-1" style={{ background: "linear-gradient(90deg, #D4AF37, transparent)" }} />
        </div>

        <div className="flex flex-col lg:flex-row gap-16 items-start">

          {/* Author Photo */}
          <div
            className={`flex-shrink-0 transition-all duration-1000 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}
          >
            <div className="relative">
              <div
                className="rounded-2xl overflow-hidden"
                style={{
                  width: "320px",
                  height: "420px",
                  border: "2px solid #D4AF37",
                  boxShadow: "8px 8px 0px #D4AF37"
                }}
              >
                <img
                  src="https://static.readdy.ai/image/be4637b51fa2c7a7dcdb39fcd9b97fd2/5dc5f9ef49e8152e09efe75d614d6d3d.jpeg"
                  alt="Carlos Alberto Díaz Martínez"
                  className="w-full h-full object-cover object-top"
                  style={{ filter: "grayscale(20%) contrast(1.05)" }}
                />
              </div>
              {/* Floating badge */}
              <div
                className="absolute -bottom-5 -right-5 rounded-xl px-5 py-3 text-center"
                style={{ background: "#002D62", border: "2px solid #D4AF37" }}
              >
                <p className="font-bold text-2xl" style={{ color: "#D4AF37", fontFamily: "'Playfair Display', serif" }}>35+</p>
                <p className="text-xs text-white/80 mt-0.5">años de experiencia</p>
              </div>
            </div>
          </div>

          {/* Text Content */}
          <div
            className={`flex-1 transition-all duration-1000 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
            style={{ transitionDelay: "0.2s" }}
          >
            <h2
              className="font-bold mb-2"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
                color: "#002D62",
                lineHeight: 1.2
              }}
            >
              Carlos Alberto Díaz Martínez
            </h2>
            <p
              className="mb-8 text-lg"
              style={{
                fontFamily: "'Playfair Display', serif",
                color: "#888",
                fontStyle: "italic"
              }}
            >
              Más allá del autor, un sobreviviente.
            </p>

            <p
              className="mb-10 leading-relaxed"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "1.05rem",
                color: "#444",
                lineHeight: 1.8
              }}
            >
              Nacido en <strong style={{ color: "#002D62" }}>Villa del Rosario, Zulia</strong>, Carlos construyó su vida sobre pilares sólidos: el Derecho, las finanzas, la tierra y la familia. Empresario ganadero, docente universitario y profesional de los seguros, su trayectoria es la de un hombre que creyó en el trabajo honesto como camino a la dignidad. Hasta que un día, todo se detuvo.
            </p>

            {/* Credentials */}
            <div className="flex flex-col gap-4 mb-10">
              {credentials.map((cred) => (
                <div
                  key={cred.title}
                  className="flex items-start gap-4 rounded-lg p-5 transition-all duration-300 cursor-default"
                  style={{
                    background: "#F8F9FA",
                    borderLeft: "4px solid #D4AF37"
                  }}
                  onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = "#F0EDD8"}
                  onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = "#F8F9FA"}
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full" style={{ background: "#002D62" }}>
                    <i className={`${cred.icon} text-lg`} style={{ color: "#D4AF37" }} />
                  </div>
                  <div>
                    <p className="font-semibold text-sm mb-1" style={{ color: "#002D62" }}>{cred.title}</p>
                    <p className="text-sm leading-relaxed" style={{ color: "#666" }}>{cred.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Family Quote */}
            <div
              className="relative px-8 py-6 rounded-xl"
              style={{
                borderTop: "1px solid #D4AF37",
                borderBottom: "1px solid #D4AF37",
                background: "linear-gradient(135deg, rgba(212,175,55,0.05), rgba(0,45,98,0.03))"
              }}
            >
              <span
                className="absolute -top-5 left-6 text-5xl leading-none"
                style={{ color: "#D4AF37", fontFamily: "'Playfair Display', serif" }}
              >
                &ldquo;
              </span>
              <p
                className="text-center"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "1.15rem",
                  color: "#002D62",
                  fontStyle: "italic",
                  lineHeight: 1.7
                }}
              >
                38 años de matrimonio. Una vida dedicada a dejar fortalezas como herencia para sus hijos y para el mundo.
              </p>
              <span
                className="absolute -bottom-5 right-6 text-5xl leading-none"
                style={{ color: "#D4AF37", fontFamily: "'Playfair Display', serif" }}
              >
                &rdquo;
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
